import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { ChartSection } from '@/financeBotV2/src/components/dashboard/ChartSection';
import { FinanceV2Section } from '@/financeBotV2/src/components/dashboard/FinanceV2Section';
import { KPIGrid } from '@/financeBotV2/src/components/dashboard/KPIGrid';
import { LineageContext, LineagePanel } from '@/financeBotV2/src/components/dashboard/LineagePanel';
import { MarkdownContent } from '@/financeBotV2/src/components/ui/markdown-content';
import { transformDashboardResponse } from '@/financeBotV2/src/services/transformers';
import { ChartData, DashboardData, KPIMetric } from '@/financeBotV2/src/types/dashboard';
import ArFilterBar, { type ArFilterValues } from '@/pages/ArFilter';
import {
  getPreCannedSapReconnectErrorCode,
  isPreCannedSapReconnectError,
  PENDING_PRE_CANNED_SAP_REFRESH_KEY,
  preCannedReportsApi,
  PreCannedReportTemplateSummary,
  withInventoryFiscalPeriod,
} from '@/services/preCannedReportsService';
import { sapOAuthService } from '@/services/sapOAuthService';
import SmartMorningLeadersDashboard, { SmartMorningControlOption } from './SmartMorningLeadersDashboard';
import SmartMorningChatInterface from './SmartMorningChatInterface';
import { buildResolvedSmartMorningPeriodOptions, buildSmartMorningPeriodOptions } from './smartMorningPeriodOptions';

const COMPARATOR_OPTIONS: SmartMorningControlOption[] = [
  { label: 'Prior Fiscal Year', value: 'Last Year' },
  { label: 'Plan / Budget', value: 'Plan / Budget' },
];

const PRE_CANNED_REPORT_CHAT_ENABLED = true;

const buildInitialParameters = (
  template?: PreCannedReportTemplateSummary | null,
): Record<string, string> => {
  if (!template) return {};

  // AR includes hidden defaults needed by its backend bindings.
  if (template.id === 'smart_morning_ar') {
    const out: Record<string, string> = Object.fromEntries(
      Object.entries(template.defaults || {}).map(([key, value]) => [
        key,
        String(value ?? ''),
      ]),
    );

    template.filters.forEach((filter) => {
      out[filter.id] = String(
        filter.default ?? out[filter.id] ?? '',
      );
    });

    return out;
  }

  // Original behavior for every other dashboard.
  const out: Record<string, string> = {};

  template.filters.forEach((filter) => {
    const defaultValue =
      filter.default ?? template.defaults?.[filter.id] ?? '';

    out[filter.id] = String(defaultValue ?? '');
  });

  return out;
};

const isPendingValidationDiagnostic = (item: Record<string, unknown>) => {
  const tool = String(item.tool ?? '');
  const source = String(item.source ?? '');
  const message = String(item.message ?? '');
  return tool.startsWith('missing.')
    || source.endsWith('_gap')
    || message.includes('Metric unavailable pending source/tool validation');
};

export default function PreCannedReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState<PreCannedReportTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [periodOptions, setPeriodOptions] = useState<SmartMorningControlOption[]>(() => buildSmartMorningPeriodOptions());
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [rawResponse, setRawResponse] = useState<Record<string, unknown> | null>(null);
  const [reportCache, setReportCache] = useState<Record<string, { signature: string; response: Record<string, unknown> }>>({});
  const [lineageContext, setLineageContext] = useState<LineageContext | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingSapRetry, setPendingSapRetry] = useState<{
    templateId: string;
    parameters: Record<string, string>;
  } | null>(null);

  useEffect(() => {
    let mounted = true;
    void buildResolvedSmartMorningPeriodOptions(preCannedReportsApi.resolvePeriod)
      .then((options) => {
        if (mounted) setPeriodOptions(options);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoadingTemplates(true);
    preCannedReportsApi.listTemplates()
      .then((items) => {
        if (!mounted) return;
        setTemplates(items);
        const first = items.find((item) => item.id === 'smart_morning_finance') ?? items[0];
        if (first) {
          setSelectedTemplateId(first.id);
          setParameters(withInventoryFiscalPeriod(buildInitialParameters(first)));
        }
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Unable to load templates');
      })
      .finally(() => {
        if (mounted) setLoadingTemplates(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [selectedTemplateId, templates],
  );

  const clearSapCallbackParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('sap_exchange');
    next.delete('sap_error');
    next.delete('message');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const beginSapReconnect = useCallback(async (
    templateIdToRefresh: string,
    parametersToRefresh: Record<string, string>,
    cause?: string | null,
  ) => {
    try {
      window.sessionStorage.setItem(
        PENDING_PRE_CANNED_SAP_REFRESH_KEY,
        JSON.stringify({
          templateId: templateIdToRefresh,
          parameters: parametersToRefresh,
          cause: cause ?? null,
        }),
      );
    } catch {
      // OAuth can still reconnect; the user may need to refresh again after returning.
    }
    setError('SAP Datasphere needs to be reconnected before SmartMorning can refresh.');
    try {
      await sapOAuthService.initiateConnection('smart-morning-for-leaders');
    } catch (err) {
      try {
        window.sessionStorage.removeItem(PENDING_PRE_CANNED_SAP_REFRESH_KEY);
      } catch {
        // No-op.
      }
      setError(err instanceof Error ? err.message : 'Unable to start SAP reconnect.');
      throw err;
    }
  }, []);

  const syncResolvedParameters = useCallback((value: Record<string, unknown>) => {
    const meta = value.meta;
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return;
    const resolved = (meta as Record<string, unknown>).parameters;
    if (!resolved || typeof resolved !== 'object' || Array.isArray(resolved)) return;
    const next = Object.fromEntries(
      Object.entries(resolved as Record<string, unknown>).map(([key, item]) => [key, String(item ?? '')]),
    );
    setParameters((current) => ({ ...current, ...next }));
  }, []);

  const parameterSignature = (value: Record<string, unknown>) => JSON.stringify(
  [
    'tool_period',
    'period',
    'period_label',
    'comparison',
    'company_codes',
    'region_scope',
    'report_currency',
    'segment',
    'as_of_date',
  ].map((key) => [key, value[key] ?? '']),
);

  const hydrate = useCallback(async (
    nextParameters = parameters,
    options: { forceRefresh?: boolean } = {},
  ) => {
    if (!selectedTemplateId || !selectedTemplate) return;
    setHydrating(true);
    setError(null);
    try {
      const enrichedParameters = withInventoryFiscalPeriod(nextParameters);
      const response = await preCannedReportsApi.hydrateTemplate(
        selectedTemplateId,
        enrichedParameters,
        { forceRefresh: Boolean(options.forceRefresh) },
        (partialResponse) => {
          syncResolvedParameters(partialResponse);
          setRawResponse(partialResponse);
          setDashboardData(transformDashboardResponse(partialResponse as Parameters<typeof transformDashboardResponse>[0]));
        },
      );
      syncResolvedParameters(response);
      setRawResponse(response);
      setDashboardData(transformDashboardResponse(response as Parameters<typeof transformDashboardResponse>[0]));
      setReportCache((current) => ({
        ...current,
        [selectedTemplateId]: { signature: parameterSignature(enrichedParameters), response },
      }));
    } catch (err) {
      if (isPreCannedSapReconnectError(err)) {
        await beginSapReconnect(
          selectedTemplateId,
          withInventoryFiscalPeriod(nextParameters),
          getPreCannedSapReconnectErrorCode(err),
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'Unable to hydrate report');
    } finally {
      setHydrating(false);
    }
  }, [beginSapReconnect, parameters, selectedTemplate, selectedTemplateId, syncResolvedParameters]);

  useEffect(() => {
    if (!selectedTemplate || Object.keys(parameters).length === 0) return;
    const cached = reportCache[selectedTemplate.id];
    if (cached?.signature === parameterSignature(parameters)) {
      setRawResponse(cached.response);
      setDashboardData(transformDashboardResponse(cached.response as Parameters<typeof transformDashboardResponse>[0]));
      setHydrating(false);
      return;
    }
    if (selectedTemplate.id !== 'smart_morning_finance') {
      void hydrate(parameters);
      return;
    }
    void preCannedReportsApi.resolvePeriod(String(parameters.tool_period || parameters.period || 'YTD'))
      .then((resolved) => {
        const next = withInventoryFiscalPeriod({
          ...parameters,
          tool_period: resolved.tool_period || parameters.tool_period,
          period_label: resolved.period_label,
          period: resolved.period,
          inventory_fiscal_period: resolved.inventory_fiscal_period,
        });
        setParameters(next);
        return hydrate(next);
      })
      .catch(() => hydrate(parameters));
  }, [selectedTemplate]);

  useEffect(() => {
    const sapExchange = searchParams.get('sap_exchange');
    const sapError = searchParams.get('sap_error');
    if (!sapExchange && !sapError) return;

    if (sapError) {
      try {
        window.sessionStorage.removeItem(PENDING_PRE_CANNED_SAP_REFRESH_KEY);
      } catch {
        // No-op.
      }
      setError(searchParams.get('message') || 'SAP Datasphere connection failed. Please try again.');
      clearSapCallbackParams();
      return;
    }

    let pending: { templateId?: string; parameters?: Record<string, string> } | null = null;
    try {
      const raw = window.sessionStorage.getItem(PENDING_PRE_CANNED_SAP_REFRESH_KEY);
      pending = raw ? JSON.parse(raw) : null;
      window.sessionStorage.removeItem(PENDING_PRE_CANNED_SAP_REFRESH_KEY);
    } catch {
      pending = null;
    }

    clearSapCallbackParams();
    if (!pending?.templateId || !pending.parameters) {
      setError(null);
      return;
    }

    setSelectedTemplateId(pending.templateId);
    setParameters(pending.parameters);
    setPendingSapRetry({
      templateId: pending.templateId,
      parameters: pending.parameters,
    });
  }, [clearSapCallbackParams, searchParams]);

  useEffect(() => {
    if (!pendingSapRetry || selectedTemplateId !== pendingSapRetry.templateId || !selectedTemplate) return;
    setPendingSapRetry(null);
    void hydrate(pendingSapRetry.parameters, { forceRefresh: true });
  }, [hydrate, pendingSapRetry, selectedTemplate, selectedTemplateId]);

  const handlePeriodChange = useCallback(async (value: string) => {
    const base = { ...parameters, tool_period: value };
    setParameters(base);
    try {
      const resolved = await preCannedReportsApi.resolvePeriod(value);
      const next = withInventoryFiscalPeriod({
        ...base,
        tool_period: resolved.tool_period || value,
        period_label: resolved.period_label,
        period: resolved.period,
        inventory_fiscal_period: resolved.inventory_fiscal_period,
      });
      setParameters(next);
      await hydrate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to resolve period');
    }
  }, [hydrate, parameters]);


  const handleArFiltersApply = useCallback(
  (values: ArFilterValues) => {
    if (selectedTemplateId !== 'smart_morning_ar') return;

    const next = {
      ...parameters,
      ...values,
    };

    setParameters(next);
    void hydrate(next, { forceRefresh: false });
  },
  [selectedTemplateId, parameters, hydrate],
  );
  const handleComparisonChange = useCallback((value: string) => {
    const next = { ...parameters, comparison: value };
    setParameters(next);
    void hydrate(next);
  }, [hydrate, parameters]);

  const handleSmartMorningTabChange = useCallback((tab: 'finance' | 'commercial' | 'ar') => {
    const templateId = tab === 'commercial' ? 'smart_morning_commercial': tab === 'ar' ? 'smart_morning_ar' : 'smart_morning_finance';
    if (templateId === selectedTemplateId || !templates.some((template) => template.id === templateId)) return;
    const cached = reportCache[templateId];
    if (cached?.signature === parameterSignature(parameters)) {
      setRawResponse(cached.response);
      setDashboardData(transformDashboardResponse(cached.response as Parameters<typeof transformDashboardResponse>[0]));
    } else {
      setRawResponse(null);
      setDashboardData(null);
    }
    setSelectedTemplateId(templateId);
  }, [parameters, reportCache, selectedTemplateId, templates]);

  const showKpiLineage = useCallback((metric: KPIMetric) => {
    setLineageContext({ kind: 'kpi', metric });
  }, []);

  const showChartLineage = useCallback((chart: ChartData) => {
    setLineageContext({ kind: 'chart', chart });
  }, []);

  const groupedCharts = dashboardData?.groups?.filter((group) => (group.charts?.length ?? 0) > 0) ?? [];
  const orphanCharts = dashboardData?.charts?.filter((chart) => (
    !groupedCharts.some((group) => group.charts.some((groupChart) => groupChart.graph_id === chart.graph_id || groupChart.title === chart.title))
  )) ?? [];
  const responseErrors = Array.isArray(rawResponse?.errors) ? rawResponse?.errors as Array<Record<string, unknown>> : [];
  const visibleResponseErrors = responseErrors.filter((item) => !isPendingValidationDiagnostic(item));
  const responseMeta = rawResponse?.meta && typeof rawResponse.meta === 'object' && !Array.isArray(rawResponse.meta)
    ? rawResponse.meta as Record<string, unknown>
    : null;
  const smartMorningTemplateId = responseMeta?.template_id;
  const isSmartMorningSelected = selectedTemplateId === 'smart_morning_finance';
  const isCommercialSelected = selectedTemplateId === 'smart_morning_commercial';
  const isOperationsSelected = selectedTemplateId === 'smart_morning_operations';
  const isArSelected = selectedTemplateId === 'smart_morning_ar';
  const isSmartMorningLeaders = responseMeta?.template_id === 'smart_morning_finance'
    || isSmartMorningSelected;
  const isSmartMorningCommercial = responseMeta?.template_id === 'smart_morning_commercial'
    || isCommercialSelected;
  const isOperationsDashboard = responseMeta?.template_id === 'smart_morning_operations'
    || isOperationsSelected;
  const isArDashboard = responseMeta?.template_id === 'smart_morning_ar'
    || isArSelected;
  const isSmartMorningDashboard = isSmartMorningLeaders || isSmartMorningCommercial || isOperationsDashboard || isArDashboard;
  const chatParameters = useMemo(() => ({
    ...(responseMeta?.parameters && typeof responseMeta.parameters === 'object' && !Array.isArray(responseMeta.parameters)
      ? responseMeta.parameters as Record<string, unknown>
      : {}),
    ...parameters,
  }), [parameters, responseMeta]);

  const openOperationsDashboard = useCallback(() => {
    const template = templates.find((item) => item.id === 'smart_morning_operations');
    if (!template) {
      setError('Operations dashboard template is not available.');
      return;
    }
    setError(null);
    setHydrating(true);
    setRawResponse(null);
    setDashboardData(null);
    setSelectedTemplateId(template.id);
    setParameters(withInventoryFiscalPeriod({
      ...buildInitialParameters(template),
      ...chatParameters,
    }));
  }, [chatParameters, templates]);

    const openArDashboard = useCallback(() => {
    const template = templates.find((item) => item.id === 'smart_morning_ar');
    if (!template) {
      setError('AR dashboard template is not available.');
      return;
    }
    setError(null);
    setHydrating(true);
    setRawResponse(null);
    setDashboardData(null);
    setSelectedTemplateId(template.id);
    const carried = Object.fromEntries(
     ['tool_period', 'period', 'period_label', 'comparison']
       .filter((key) => chatParameters[key] !== undefined && chatParameters[key] !== null)
       .map((key) => [key, chatParameters[key]]),
    );
    setParameters(withInventoryFiscalPeriod({
      ...buildInitialParameters(template),
      ...carried,
    }));
  }, [chatParameters, templates]);

  const openFinanceDashboard = useCallback(() => {
    const template = templates.find((item) => item.id === 'smart_morning_finance');
    if (!template) {
      setError('Finance dashboard template is not available.');
      return;
    }
    setError(null);
    setHydrating(true);
    setRawResponse(null);
    setDashboardData(null);
    setSelectedTemplateId(template.id);
    setParameters(withInventoryFiscalPeriod({
      ...buildInitialParameters(template),
      ...chatParameters,
    }));
  }, [chatParameters, templates]);

  const handleForceRefresh = useCallback(() => {
    void hydrate(parameters, { forceRefresh: true });
  }, [hydrate, parameters]);

  const genericDashboardContent = (
    <>
      {dashboardData?.summaryText && (
        <FinanceV2Section title={dashboardData.title || selectedTemplate?.title || 'Summary'}>
          <MarkdownContent content={dashboardData.summaryText} className="text-sm leading-relaxed text-[#3F3338]" />
        </FinanceV2Section>
      )}

      {dashboardData?.metrics?.length ? (
        <KPIGrid metrics={dashboardData.metrics} onShowLineage={showKpiLineage} />
      ) : null}

      {groupedCharts.map((group) => (
        <ChartSection
          key={group.category}
          charts={group.charts}
          botMessageId="pre-canned-report"
          onShowChartLineage={showChartLineage}
        />
      ))}

      {orphanCharts.length > 0 && (
        <ChartSection
          charts={orphanCharts}
          botMessageId="pre-canned-report"
          onShowChartLineage={showChartLineage}
        />
      )}
    </>
  );

  return (
    <div className="min-h-full bg-[#FAF7F1] text-[#1F1A20]">
      <div className="flex w-full max-w-none flex-col gap-5 px-5 py-5 lg:px-7">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {visibleResponseErrors.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {visibleResponseErrors.map((item, index) => (
              <div key={`${String(item.source ?? 'error')}-${index}`}>
                {String(item.source ?? 'Source')}: {String(item.message ?? 'Hydration warning')}
              </div>
            ))}
          </div>
        )}

        {(loadingTemplates || hydrating) && (
          <FinanceV2Section title="Loading">
            <p className="text-sm text-[#7B6B72]">Checking for updates...</p>
          </FinanceV2Section>
        )}

        {isSmartMorningDashboard && rawResponse ? (
          <>
            <SmartMorningLeadersDashboard
              key={String(smartMorningTemplateId)}
              response={rawResponse}
              arFilterControls={
                selectedTemplateId === 'smart_morning_ar' &&
                selectedTemplate?.id === 'smart_morning_ar' ? (
                  <ArFilterBar
                    filters={selectedTemplate.filters}
                    parameters={parameters}
                    disabled={hydrating || loadingTemplates}
                    onApply={handleArFiltersApply}
                  />
                ) : undefined
              }
              periodOptions={periodOptions}
              comparisonOptions={COMPARATOR_OPTIONS}
              onPeriodChange={handlePeriodChange}
              onComparisonChange={handleComparisonChange}
              controlsDisabled={hydrating}
              initialTab={smartMorningTemplateId === 'smart_morning_commercial' ? 'commercial' : 'finance'}
              onTabChange={handleSmartMorningTabChange}
              loading={hydrating}
              onForceRefresh={handleForceRefresh}
              forceRefreshDisabled={loadingTemplates || hydrating || !selectedTemplate}
              activeTab={isArSelected ? 'ar' : isOperationsSelected ? 'operations' : isCommercialSelected ? 'commercial' : 'finance'}
              onFinanceClick={openFinanceDashboard}
              onOperationsClick={openOperationsDashboard}
              onArClick={openArDashboard}
              contentOverride={isOperationsSelected || isArSelected ? genericDashboardContent : undefined}
            />
            {/* Pre-canned report chat UI is temporarily disabled; keep the component wiring for later reactivation. */}
            {isSmartMorningDashboard && PRE_CANNED_REPORT_CHAT_ENABLED && (
              <SmartMorningChatInterface parameters={chatParameters} cacheReady={!hydrating} templateId={selectedTemplateId} />
            )}
          </>
        ) : null}

        {!hydrating && !isSmartMorningDashboard && (
          <>
            {genericDashboardContent}
          </>
        )}

        {!loadingTemplates && !hydrating && !dashboardData && !error && (
          <FinanceV2Section title="No Report Loaded">
            <p className="text-sm text-[#7B6B72]">No pre-canned report template is available.</p>
          </FinanceV2Section>
        )}
      </div>

      <LineagePanel context={lineageContext} onClose={() => setLineageContext(null)} />
    </div>
  );
}
